import { useCallback, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { map } from "lodash"
import { Popover, List, ListSubheader, ListItemButton, ListItemText, Typography } from "@mui/material"
import CheckIcon from "@mui/icons-material/Check"
import MetricsSearch from "./metrics-search"
import { processMetrics, filterProcessedMetrics } from "./helpers"
import "./add-series-popover.scss"

const EMPTY_ARRAY = []

// Popover variant of the shared custom-metrics-selector: a single-click add-one-series list reusing the Drawer's search/category plumbing; an already-plotted metric renders disabled with an "Already shown" tag.
export default function AddSeriesPopover({
  open,
  anchorEl,
  onClose,
  availableMetricsGroups,
  alreadyShownMetricIds,
  onSelect,
}) {
  const [searchValue, setSearchValue] = useState("")
  // MetricsSearch never re-syncs its display value to a prop reset after mount, so bump this key to remount it whenever searchValue is cleared.
  const [searchResetKey, setSearchResetKey] = useState(0)

  const alreadyShownIdsArray = useMemo(
    () => Array.from(alreadyShownMetricIds ?? EMPTY_ARRAY),
    [alreadyShownMetricIds],
  )

  const { processedAvailableMetrics } = useMemo(
    () => processMetrics(availableMetricsGroups, alreadyShownIdsArray),
    [availableMetricsGroups, alreadyShownIdsArray],
  )

  const filteredGroups = useMemo(
    () => filterProcessedMetrics(processedAvailableMetrics, searchValue),
    [processedAvailableMetrics, searchValue],
  )

  const groups = useMemo(() => Object.values(filteredGroups ?? {}), [filteredGroups])

  const handleSelect = useCallback((metricId) => {
    onSelect(metricId)
    // Clear the search so it doesn't carry over next open -- the popover never unmounts between opens.
    setSearchValue("")
    setSearchResetKey((key) => key + 1)
  }, [onSelect])

  const handleClose = useCallback(() => {
    setSearchValue("")
    setSearchResetKey((key) => key + 1)
    onClose()
  }, [onClose])

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      classes={{ paper: "components_custom-metrics-selector-add-series-popover" }}
    >
      <div className="add-series-popover-header">
        <Typography variant="subtitle2">Add series</Typography>
        <MetricsSearch
          key={searchResetKey}
          shouldFocus={open}
          metricsSearchValue={searchValue}
          setMetricsSearchValue={setSearchValue}
        />
      </div>
      <div className="add-series-popover-list">
        {groups.length === 0 && (
          <Typography variant="body2" className="add-series-popover-empty">
            {searchValue ? "No metrics match your search." : "No metrics available to add."}
          </Typography>
        )}
        {groups.map(group => (
          <List
            key={group.id}
            disablePadding
            dense
            subheader={<ListSubheader disableSticky>{group.label}</ListSubheader>}
          >
            {map(group.items, item => (
              <ListItemButton
                key={item.id}
                disabled={item.selected}
                onClick={() => handleSelect(item.selector)}
                className="add-series-popover-item"
              >
                <ListItemText primary={item.label} />
                {item.selected && (
                  <span className="add-series-popover-already-shown">
                    <CheckIcon fontSize="inherit" /> Already shown
                  </span>
                )}
              </ListItemButton>
            ))}
          </List>
        ))}
      </div>
    </Popover>
  )
}

AddSeriesPopover.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  availableMetricsGroups: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })).isRequired,
  })),
  alreadyShownMetricIds: PropTypes.instanceOf(Set),
  onSelect: PropTypes.func.isRequired,
}
